import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { Scenario, Package } from '../models';
import { ScenarioService } from '../services/scenario.service';
import { ScenarioExportService } from '../services/scenario-export.service';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastService } from 'src/app/toast/toast.service';
import { ValidationMessagesHelper } from '../../test-recorder/helpers/validation-messages.helper';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { StepsService } from '../../steps/services/steps.service';
import { Step } from '../../steps/models';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AuthService } from '../../../auth/login/services/auth.service';
import { AuthProfileService } from '../../auth-profile/services/auth-profile.service';
import { AuthProfile } from 'src/app/shared/models/auth-profile.interface';
import { ScreenMasterService } from '../../screen-master/services/screen-master.service';
import { Screen } from 'src/app/shared/models/screen.interface';

export class ScenarioViewModel extends MasterViewModel<Scenario> {
  scenarioDialog: boolean = false;
  scenarios = signal<Scenario[]>([]);
  selectedClientFilter = signal<string>('');
  selectedFunctionalAreaFilter = signal<string>('');
  selectedTestTypeFilter = signal<string>('');
  allScenarios = signal<Scenario[]>([]); // Store unfiltered scenarios
  selectedScenario: Scenario | null = null;
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  type_test: any[] = [];
  all_function_areas: any[] = []; // All functional areas from API
  filtered_function_area: any[] = []; // Filtered based on client
  clients = signal<any[]>([]); // Changed to signal
  clientOptions = signal<{ label: string, value: string }[]>([]); // Added
  scenariosList: { label: string, value: string }[] = [];
  authProfiles = signal<AuthProfile[]>([]); // Added for auth profiles
  groupedAuthProfiles = signal<any[]>([]); // Grouped auth profiles by profileGroup
  profileGroupOptions = signal<{ label: string, value: string }[]>([]); // Profile group dropdown options
  screenMasters = signal<Screen[]>([]); // Added for screen masters
  expandPreconditions: number | null = null; // Control accordion expansion (0 = open, null = closed)
  private tableInstance: Table | null = null;
  isClientDropdownDisabled = signal<boolean>(false);
  filterTestType = signal<string[]>([]);
  private _active = false;

  // Functional Area Tabs
  activeFaTab = signal<string>('all');
  faTabCounts = signal<{ name: string; id: string; count: number }[]>([]);
  visibleTabLimit: number = 8;
  showMoreDropdown = signal<boolean>(false);
  moreDropdownSearch: string = '';

  // Pagination state
  totalScenarios = signal<number>(0);
  allScenariosCount = signal<number>(0);
  private _allClientScenarios: Scenario[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  searchTerm: string = '';
  sortField: string = 'scenarioName';
  sortOrder: string = 'ASC';
  isLoadingScenarios: boolean = false;
  private searchDebounceTimer: any = null;
  private _initialScenariosLoaded: boolean = false;
  private _functionalAreasLoading: boolean = false;

  constructor(
    http: HttpClient,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private scenarioService: ScenarioService,
    private exportService: ScenarioExportService,
    private router: Router,
    private route: ActivatedRoute,
    private functionalAreaService: FuntionalAreaService,
    private stepsService: StepsService,
    private authService: AuthService,
    private authProfileService: AuthProfileService,
    private screenMasterService: ScreenMasterService
  ) {

    const baseUrl = `${environment.apiUrl}/scenarios`;
    const moduleName = 'Scenario';
    super(http, baseUrl, moduleName);
    this.createScenarioForm();

  }

  public destroy(): void {
    this._active = false;
  }

  public init(): void {
    this._active = true;
    this.loadAuthProfiles();
    this.loadScreenMasters();

    const params = this.route.snapshot.queryParams;
    if (!params['testTypeId']) sessionStorage.removeItem('scenario_testTypeId');
    if (!params['functionalAreaId']) sessionStorage.removeItem('scenario_functionalAreaId');

    this.loadReferenceData().subscribe({
      next: () => {
        this.loadClientsAndScenarios();
      },
      error: () => {
        this.loadClientsAndScenarios();
      }
    });
  }

  private loadClientsAndScenarios(): void {
    this.scenarioService.getUserClients().subscribe({
      next: (response: any) => {
        const clients = response?.data || [];
        const clientMappedOptions = clients.map((c: any) => ({ label: c.name, value: c.id }));
        const opts = this.isSystemUser()
          ? [{ label: 'Master Client', value: 'default' }, ...clientMappedOptions]
          : clientMappedOptions;
        this.clients.set(clients);
        this.clientOptions.set(opts);

        const params = this.route.snapshot.queryParams;
        const queryClientId = params['clientId'];
        const hasFilterParams = !!(params['functionalAreaId'] || params['testTypeId']);

        if (this.isSystemUser()) {
          this.isClientDropdownDisabled.set(false);
          const clientToLoad = queryClientId || 'default';
          this.selectedClientFilter.set(clientToLoad);
          if (clientToLoad) sessionStorage.setItem('scenario_clientId', clientToLoad);
          this.proceedWithInitialization();
        } else if (clients.length === 1) {
          const singleClientId = clients[0].id;
          this.isClientDropdownDisabled.set(true);
          this.selectedClientFilter.set(singleClientId);
          sessionStorage.setItem('scenario_clientId', singleClientId);
          this.proceedWithInitialization();
        } else if (clients.length > 1) {
          this.isClientDropdownDisabled.set(false);
          if (queryClientId) {
            this.selectedClientFilter.set(queryClientId);
            sessionStorage.setItem('scenario_clientId', queryClientId);
          }
          this.proceedWithInitialization();
        } else {
          this.proceedWithInitialization();
        }
      },
      error: (error) => {
        this.toastService.error('Failed to load clients');
      }
    });
  }

  public getTypeTestOptions() {
    return this.type_test.map(item => ({
      value: item.referenceValue,
      id: item.id,
    }));
  }

  public getFunctionAreaOptions() {
    return this.filtered_function_area.map(item => ({
      value: item.name || item.referenceValue,
      id: item.id,
    }));
  }

  /**
   * Get functional area options with test type appended for display
   */
  get functionalAreaOptionsWithTestType(): any[] {
    let options = this.filtered_function_area || [];
    
    // Filter by test types if selected (multiple)
    const selectedTypes = this.filterTestType();
    if (selectedTypes && selectedTypes.length > 0) {
      options = options.filter((fa: any) => 
        selectedTypes.includes(fa.testType?.id)
      );
    }
    
    return options.map((fa: any) => ({
      ...fa,
      displayName: fa.name + (fa.testType?.referenceValue ? ` (${fa.testType.referenceValue})` : '')
    }));
  }

  /**
   * Get filtered functional areas with test type appended for display
   */
  get filteredFunctionalAreasWithTestType(): any[] {
    let filtered = this.filtered_function_area || [];
    
    // Additional filter by test types if selected (multiple)
    const selectedTypes = this.filterTestType();
    if (selectedTypes && selectedTypes.length > 0) {
      filtered = filtered.filter((fa: any) => 
        selectedTypes.includes(fa.testType?.id)
      );
    }
    
    return filtered.map((fa: any) => ({
      ...fa,
      displayName: fa.name + (fa.testType?.referenceValue ? ` (${fa.testType.referenceValue})` : '')
    }));
  }

  // New methods for nested filtering
  public functionalAreaFilterOptions() {
    const selectedTestTypes = this.filterTestType();

    // If no test types selected, show all functional areas (needed when navigating with only functionalAreaId)
    if (!selectedTestTypes || selectedTestTypes.length === 0) {
      const source = this.filtered_function_area?.length > 0
        ? this.filtered_function_area
        : this.getClientFilteredFunctionalAreas();
      const seen = new Set<string>();
      return source.map((item: any) => {
        const name = item?.name || item?.referenceValue || 'Unnamed';
        const testTypeName = item?.testType?.referenceValue || item?.testType?.name || '';
        return {
          label: testTypeName ? `${name} (${testTypeName})` : name,
          value: item?.id
        };
      }).filter((item: any) => {
        if (!item.value || seen.has(item.label)) return false;
        seen.add(item.label);
        return true;
      });
    }

    // Return all functional areas if filtered_function_area is empty
    if (!this.filtered_function_area || this.filtered_function_area.length === 0) {
      return [];
    }

    // Filter functional areas: check both testType.id and testTypeId properties
    const filteredAreas = this.filtered_function_area.filter((item: any) => {
      if (!item) return false;

      // Extract testTypeId from multiple possible locations
      const testTypeId = item.testType?.id || item.testTypeId || item.testType;

      // Check if this functional area's test type matches any selected test type
      return testTypeId && selectedTestTypes.includes(testTypeId);
    });
    
    const seenFiltered = new Set<string>();
    return filteredAreas.map(item => {
      const testTypeName = item.testType?.referenceValue || item.testType?.name || '';
      const displayLabel = testTypeName
        ? `${item.name || item.referenceValue || 'Unnamed'} (${testTypeName})`
        : item.name || item.referenceValue || 'Unnamed';

      return {
        label: displayLabel,
        value: item.id
      };
    }).filter(item => {
      if (seenFiltered.has(item.label)) return false;
      seenFiltered.add(item.label);
      return true;
    });
  }


  private proceedWithInitialization(): void {
    const params = this.route.snapshot.queryParams;
    const clientId = params['clientId'];

    if (!clientId && this.isSystemUser()) {
      this.selectedClientFilter.set('default');
      sessionStorage.setItem('scenario_clientId', 'default');
    }

    // Fire counts immediately in parallel (don't wait for functional areas)
    this.loadFaTabCounts();

    this.loadUserFunctionalAreas(() => {
      this.handleQueryParamsChange(this.route.snapshot.queryParams);
    });
  }

  private handleQueryParamsChange(params: any): void {
    const clientId = params['clientId'];
    const functionalAreaId = params['functionalAreaId'];
    const testTypeId = params['testTypeId'];

    if (clientId || functionalAreaId || testTypeId) {
      if (clientId) {
        this.selectedClientFilter.set(clientId);
        sessionStorage.setItem('scenario_clientId', clientId);
      }

      if (testTypeId) {
        const testTypeArray = testTypeId.split(',').map((id: string) => id.trim()).filter((id: string) => id);
        this.filterTestType.set(testTypeArray);
        sessionStorage.setItem('scenario_testTypeId', testTypeId);
      }

      if (functionalAreaId) {
        this.selectedFunctionalAreaFilter.set(functionalAreaId);
        sessionStorage.setItem('scenario_functionalAreaId', functionalAreaId);
      }

      const currentClientId = clientId || this.selectedClientFilter();
      this.loadDataFromQueryParams(currentClientId, functionalAreaId, testTypeId);
      return;
    }

    // No query params - just load scenarios for current client
    sessionStorage.removeItem('scenario_functionalAreaId');
    sessionStorage.removeItem('scenario_testTypeId');

    const currentClientId = this.selectedClientFilter();
    if (currentClientId) {
      this.filtered_function_area = this.getClientFilteredFunctionalAreas();
      this.currentPage = 1;
      this.loadScenariosPaginated();
    }
  }

  private loadDataFromQueryParams(clientId?: string, functionalAreaId?: string, testTypeId?: string): void {
    if (!clientId) {
      this.allScenarios.set([]);
      this.scenarios.set([]);
      return;
    }

    this.selectedClientFilter.set(clientId);

    if (testTypeId) {
      this.applyFunctionalAreaFilter(clientId);
    } else {
      this.filtered_function_area = this.getClientFilteredFunctionalAreas(clientId);
    }

    this.currentPage = 1;
    this.loadScenariosPaginated();
  }


  private createScenarioForm(): void {
    this.createForm({
      testTypeId: [null, [this.validators.required]],
      functionalAreaId: [null, [this.validators.required]],
      scenarioId: [null, [this.validators.required]],
      scenarioName: [null, [this.validators.required, this.validators.maxLength(255)]],
      scenarioDescription: [null],
      authProfileId: [null],
      selectedProfileGroup: [null],
      screenId: [null],
      sessionPolicy: [null],
      skipIfSatisfied: [false]
    });
  }

  public viewScenario(scenarioData: Scenario): void {
    this.selectedScenario = scenarioData;
    this.isEditMode = false;
    this.isViewMode = true;
    this.scenarioDialog = true;

    // Parse authProfileInfo to get profileGroup
    let profileGroup: string | null = null;
    if ((scenarioData as any).authProfileInfo) {
      try {
        const info = typeof (scenarioData as any).authProfileInfo === 'string'
          ? JSON.parse((scenarioData as any).authProfileInfo)
          : (scenarioData as any).authProfileInfo;
        profileGroup = info?.profileGroup || null;
      } catch (e) {
        console.warn('Failed to parse authProfileInfo', e);
      }
    }

    this.form.patchValue({
      scenarioName: scenarioData.scenarioName,
      scenarioId: scenarioData.scenarioId,
      testTypeId: scenarioData.testTypeId,
      functionalAreaId: scenarioData.functionalAreaId,
      scenarioDescription: scenarioData.scenarioDescription,
      authProfileId: scenarioData.authProfileId || null,
      selectedProfileGroup: profileGroup,
      screenId: scenarioData.screenId || null,
      sessionPolicy: scenarioData.sessionPolicy,
      skipIfSatisfied: scenarioData.skipIfSatisfied || false
    });

    this.form.disable();
  }

  public editScenario(scenarioData: Scenario): void {
    this.selectedScenario = scenarioData;
    this.isEditMode = true;
    this.isViewMode = false;
    this.scenarioDialog = true;

    this.filtered_function_area = this.getClientFilteredFunctionalAreas();

    // Load auth profiles for the functional area in edit mode
    if (scenarioData.functionalAreaId) {
      this.loadAuthProfiles(scenarioData.functionalAreaId);
    }
    if (this.all_function_areas.length === 0) {
      this.loadUserFunctionalAreas(() => {
        // Apply client-based filtering (same as openNew)
        const currentClientId = this.selectedClientFilter();
        this.applyFunctionalAreaFilter(currentClientId);
        this.populateEditForm(scenarioData);
      });
    } else {
      // Apply client-based filtering (same as openNew)
      const currentClientId = this.selectedClientFilter();
      this.applyFunctionalAreaFilter(currentClientId);
      this.populateEditForm(scenarioData);
    }
  }

  private populateEditForm(scenarioData: Scenario): void {
    // Parse authProfileInfo to get profileGroup
    let profileGroup: string | null = null;
    if ((scenarioData as any).authProfileInfo) {
      try {
        const info = typeof (scenarioData as any).authProfileInfo === 'string'
          ? JSON.parse((scenarioData as any).authProfileInfo)
          : (scenarioData as any).authProfileInfo;
        profileGroup = info?.profileGroup || null;
      } catch (e) {
        console.warn('Failed to parse authProfileInfo', e);
      }
    }

    const hasPreconditions = !!(profileGroup || scenarioData.authProfileId || scenarioData.screenId);
    this.expandPreconditions = hasPreconditions ? 0 : null;

    this.form.patchValue({
      scenarioName: scenarioData.scenarioName,
      scenarioDescription: scenarioData.scenarioDescription,
      scenarioId: scenarioData.scenarioId,
      testTypeId: scenarioData.testTypeId,
      functionalAreaId: scenarioData.functionalAreaId,
      // Preconditions fields
      authProfileId: scenarioData.authProfileId || null,
      selectedProfileGroup: profileGroup,
      screenId: scenarioData.screenId || null,
      sessionPolicy: scenarioData.sessionPolicy,
      skipIfSatisfied: scenarioData.skipIfSatisfied || false
    });

    // Disable Functional Area and Test Type dropdowns in edit mode
    this.form.get('functionalAreaId')?.disable();
    this.form.get('testTypeId')?.disable();
  }

  private loadReferenceData(): Observable<any> {
    return this.scenarioService.getReferenceList(['TEST_TYPE']).pipe(
      tap((dataResult: any) => {
        this.type_test = dataResult;
      }),
      shareReplay(1)
    );

    // Load all scenarios for the dropdown
    // this.scenarioService.getScenariosByStatus(true).subscribe({
    //   next: (scenarios: Scenario[]) => {
    //     this.scenariosList = scenarios.map(scenario => ({
    //       label: `${scenario.scenarioId || ''} - ${scenario.scenarioName}`,
    //       value: scenario.scenarioName
    //     }));
    //   },
    //   error: (error) => {
    //     this.toastService.error('Failed to load scenarios');
    //   }
    // });
  }

  public openNew(): void {
    this.selectedScenario = null;
    this.isEditMode = false;
    this.isViewMode = false;
    this.scenarioDialog = true;
    this.expandPreconditions = null;
    this.form.reset();

    // Enable Functional Area and Test Type dropdowns in create mode
    this.form.get('functionalAreaId')?.enable();
    this.form.get('testTypeId')?.enable();

    const currentClientId = this.selectedClientFilter();

    if (this.all_function_areas.length === 0) {
      this.loadUserFunctionalAreas(() => {
        this.applyFunctionalAreaFilter(currentClientId);
        this.preselectFunctionalArea();
      });
    } else {
      this.applyFunctionalAreaFilter(currentClientId);
      this.preselectFunctionalArea();
    }
  }

  private preselectFunctionalArea(): void {
    const selectedFunctionalAreaId = this.selectedFunctionalAreaFilter();
    if (selectedFunctionalAreaId) {
      this.form.patchValue({
        functionalAreaId: selectedFunctionalAreaId
      });
    }
  }

  private applyFunctionalAreaFilter(clientId: string): void {
    this.filtered_function_area = this.getClientFilteredFunctionalAreas(clientId);
  }

  private getClientFilteredFunctionalAreas(clientId?: string): any[] {
    const cid = clientId || this.selectedClientFilter();
    if (!cid) {
      return this.isSystemUser()
        ? this.all_function_areas.filter(area => area.clientPackageMappingId === null)
        : [];
    }
    if (cid === 'default') {
      return this.all_function_areas.filter(area => area.clientPackageMappingId === null);
    }
    return this.all_function_areas.filter(area => area.clientPackageMapping?.clientId === cid);
  }

  public saveScenario(): void {
    if (!this.validateForm()) return;
    const formValue = this.form.getRawValue();
    const selectedGroup = formValue.selectedProfileGroup || null;
    // Build authProfileInfo with profileGroup + all authProfileIds from that group
    let authProfileInfo: string | null = null;
    if (selectedGroup) {
      const group = this.groupedAuthProfiles().find(g => g.value === selectedGroup);
      const ids = (group?.items || []).map((p: any) => p.id);
      authProfileInfo = JSON.stringify({ profileGroup: selectedGroup, authProfileIds: ids });
    }
    const payload: any = {
      scenarioId: formValue.scenarioId,
      scenarioName: formValue.scenarioName,
      scenarioDescription: formValue.scenarioDescription,
      functionalAreaId: formValue.functionalAreaId,
      testTypeId: formValue.testTypeId,
      authProfileId: null,
      screenId: formValue.screenId || null,
      sessionPolicy: formValue.sessionPolicy || null,
      skipIfSatisfied: formValue.skipIfSatisfied ?? false,
      authProfileInfo
    };

    if (this.isEditMode && this.selectedScenario) {

      this.scenarioService.updateScenario(this.selectedScenario.id, payload).subscribe({
        next: (res) => {
          this.toastService.success('Scenario updated successfully');
          this.hideDialog();
          this.refreshCurrentView();
        },
        error: (error) => {
          this.toastService.error(error.error?.message || 'Failed to update scenario');
        }
      });
    } else {

      this.scenarioService.createScenario(payload).subscribe({
        next: (res) => {
          this.hideDialog();
          this.refreshCurrentView();
          if (res && res?.id) {
          }
        },
        error: (error) => {
          this.toastService.error(error.error?.message || 'Failed to create scenario');
        }
      });
    }
  }

  public deleteScenario(scenarioData: Scenario): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the scenario "${scenarioData.scenarioName}"?`,
      header: 'Confirm',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-warn',
      defaultFocus: 'reject',
      accept: () => {
        this.scenarioService.deleteScenario(scenarioData.id).subscribe({
          next: () => {
            this.toastService.success('Scenario deleted successfully');
            this.refreshCurrentView();
          },
          error: (error) => {
            this.toastService.error(error.error?.message || 'Failed to delete scenario');
          }
        });
      }
    });
  }

  private refreshCurrentView(): void {
    this.loadScenariosPaginated();
    this.loadFaTabCounts();
  }

  public captureSteps(scenarioData: Scenario): void {
    if (scenarioData.recordingStatus === 'inactive') {
      this.toastService.error('Recording has been removed. Create a new recording to capture steps.');
    }

    this.checkExistingSteps(scenarioData.id).subscribe({
      next: (stepInfo) => {
        if (stepInfo.hasSteps && stepInfo.source === 'recording') {
          this.confirmStepSourceChange(
            'Recording',
            stepInfo.stepCount,
            () => this.proceedToConversationalAI(scenarioData, true, 'recording')
          );
        } else {
          this.proceedToConversationalAI(scenarioData, false, null);
        }
      },
      error: (err) => {
        this.toastService.error('Cannot proceed: Failed to verify existing steps. Please try again.');
      }
    });
  }

  private proceedToConversationalAI(scenarioData: Scenario, deleteOldSteps: boolean, oldStepSource: string | null): void {
    this.router.navigate(['scenario/capture', scenarioData.functionalAreaId, scenarioData.id], {
      queryParams: {
        deleteOldSteps: deleteOldSteps ? 'true' : 'false',
        oldStepSource: oldStepSource
      }
    });
  }

  // Quick action: navigate to Recording create page
  public navigateToRecordingCreate(scenarioData?: Scenario): void {

    if (!scenarioData) {
      this.router.navigate(['/test-studio/recorder-record']);
      return;
    }
    const scenario = scenarioData;
    // Check if Conversational AI steps exist before proceeding
    this.checkExistingSteps(scenario.id).subscribe({
      next: (stepInfo) => {
        if (stepInfo.hasSteps && stepInfo.source === 'conversational') {
          // Warn only when switching from Conversational AI to Recording
          this.confirmStepSourceChange(
            'Conversational AI',
            stepInfo.stepCount,
            () => this.proceedToRecording(scenario, true, 'conversational')
          );
        } else {
          // Recording steps already exist or no steps - just navigate
          this.proceedToRecording(scenario, false, null);
        }
      },
      error: (err) => {
        this.toastService.error('Cannot proceed: Failed to verify existing steps. Please try again.');
      }
    });
  }

  private proceedToRecording(scenarioData: Scenario, deleteOldSteps: boolean, oldStepSource: string | null): void {


    if (scenarioData.recordingId) {
      this.scenarioService.getRecordingById(scenarioData.recordingId).subscribe({
        next: (recording: any) => {
          if (recording) {
            this.router.navigate(['/test-studio/recorder-record'], {
              queryParams: { scenarioId: scenarioData.id, from: 'scenario' },
              state: {
                recording,
                editRecordingId: recording.id,
                isCloneMode: false,
                packageId: scenarioData.functionalArea?.clientPackageMapping?.package?.id || '',
                functionalAreaId: scenarioData.functionalAreaId,
                scenarioId: scenarioData.scenarioId,
                clientId: scenarioData.functionalArea?.clientPackageMapping?.clientId || this.selectedClientFilter() || '',
                deleteOldSteps: deleteOldSteps,
                oldStepSource: oldStepSource
              }
            });
          } else {
            this.navigateToNewRecording(scenarioData, deleteOldSteps, oldStepSource);
          }
        },
        error: () => {
          this.toastService.error('Failed to load recording');
          this.navigateToNewRecording(scenarioData, deleteOldSteps, oldStepSource);
        }
      });
    } else {
      // No existing recordingId - navigate to create new recording
      this.navigateToNewRecording(scenarioData, deleteOldSteps, oldStepSource);
    }
  }

  private navigateToNewRecording(scenarioData: Scenario, deleteOldSteps: boolean = false, oldStepSource: string | null = null): void {
    this.router.navigate(['/test-studio/recorder-record'], { 
      queryParams: {
        scenarioId: scenarioData.id,
        from: 'scenario'
      },
      state: {
        packageName: scenarioData.functionalArea?.package?.packageName,
        packageId: scenarioData.functionalArea?.clientPackageMapping?.packageId || '',
        functionalAreaId: scenarioData.functionalAreaId,
        testTypeName: scenarioData.testType?.referenceValue,
        scenarioName: scenarioData.scenarioName || '',
        scenarioDescription: scenarioData.scenarioDescription || '',
        packageDefinitionId: scenarioData.functionalArea?.package?.id || '',
        testTypeId: scenarioData.testType?.id || '',
        scenarioIdField: scenarioData.scenarioId || '',
        clientId: scenarioData.functionalArea?.clientPackageMapping?.clientId || this.selectedClientFilter() || '',
        deleteOldSteps: deleteOldSteps,
        oldStepSource: oldStepSource
      }
      
    });
    
  }

  private checkExistingSteps(scenarioId: string): Observable<{
    hasSteps: boolean;
    source: 'recording' | 'conversational' | null;
    stepCount: number;
  }> {
    return this.stepsService.getStepsByScenario(scenarioId).pipe(
      map((response: any): { hasSteps: boolean; source: 'recording' | 'conversational' | null; stepCount: number } => {
        const steps: Step[] = response?.data || [];
        
        if (steps.length === 0) {
          return { hasSteps: false, source: null, stepCount: 0 };
        }
        
        let isRecording = steps[0].isRecording === true;
        if (!isRecording && steps[0].additionalInfo) {
          try {
            const additionalInfo = JSON.parse(steps[0].additionalInfo);
            isRecording = additionalInfo.recordedStep === true;
          } catch (e) {
          }
        }
        
        const source: 'recording' | 'conversational' = isRecording ? 'recording' : 'conversational';
        
        return {
          hasSteps: true,
          source: source,
          stepCount: steps.length
        };
      }),
      catchError((error) => {
        return of({ hasSteps: false, source: null, stepCount: 0 });
      })
    );
  }

 
  private confirmStepSourceChange(
    existingSource: string,
    stepCount: number,
    onConfirm: () => void
  ): void {
    this.confirmationService.confirm({
      header: 'Warning : Steps Already Exist',
      message: `This scenario already has ${stepCount} step(s) recorded via <strong>${existingSource}</strong>.<br/><br/>
                Do you want to proceed? <br/><br/>
                <span class="text-red-600">Existing steps will be replaced when you save.</span>`,
   
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      defaultFocus: 'reject',
      accept: () => {
        onConfirm();
      },
      reject: () => {
        // User cancelled, do nothing
      }
    });
  }

  public hideDialog(): void {
    this.selectedScenario = null;
    this.scenarioDialog = false;
    this.isEditMode = false;
    this.isViewMode = false;
    this.expandPreconditions = null;
    this.form.reset();
    this.form.enable();

    this.filtered_function_area = this.getClientFilteredFunctionalAreas();
  }

  public onGlobalFilter(_table: Table, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.searchTerm = value;
      this.currentPage = 1;
      this.loadScenariosPaginated();
      this.rebuildFaTabCounts();
    }, 400);
  }

  public onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    const trimmed = (value || '').trim();
    if (trimmed.length > 0 && trimmed.length < 3) return;
    this.searchDebounceTimer = setTimeout(() => {
      this.searchTerm = value;
      this.currentPage = 1;
      this.loadScenariosPaginated();
      this.rebuildFaTabCounts();
    }, 700);
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadScenariosPaginated();
    this.rebuildFaTabCounts();
  }

  public onLazyLoad(event: any): void {
    if (!this._initialScenariosLoaded) {
      this._initialScenariosLoaded = true;
      return;
    }
    this.currentPage = Math.floor((event.first || 0) / (event.rows || this.pageSize)) + 1;
    this.pageSize = event.rows || this.pageSize;
    if (event.sortField) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
    }
    this.loadScenariosPaginated();
  }

  public loadScenariosPaginated(): void {
    const clientId = this.selectedClientFilter();
    if (!clientId) return;

    this.isLoadingScenarios = true;

    const activeFa = this.activeFaTab();
    const functionalAreaFilter = activeFa && activeFa !== 'all'
      ? activeFa
      : (this.selectedFunctionalAreaFilter() || undefined);

    this.scenarioService.getScenariosPaginated(clientId, {
      page: this.currentPage,
      pageSize: this.pageSize,
      search: this.searchTerm || undefined,
      functionalArea: functionalAreaFilter,
      testType: this.filterTestType().join(',') || undefined,
      sortBy: this.sortField,
      sortOrder: this.sortOrder,
    }).subscribe({
      next: (result) => {
        this.scenarios.set(result.data);
        this.totalScenarios.set(result.total);
        this.isLoadingScenarios = false;
      },
      error: () => {
        this.toastService.error('Failed to load scenarios.');
        this.scenarios.set([]);
        this.totalScenarios.set(0);
        this.isLoadingScenarios = false;
      }
    });
  }

  public loadFaTabCounts(): void {
    const clientId = this.selectedClientFilter();
    if (!clientId) {
      this.faTabCounts.set([]);
      this._allClientScenarios = [];
      this.allScenariosCount.set(0);
      return;
    }

    // Show cached counts immediately while fetching fresh data
    const cacheKey = `scenario_faCounts_${clientId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        this.faTabCounts.set(parsed.tabs);
        this.allScenariosCount.set(parsed.total);
      } catch { /* ignore corrupt cache */ }
    }

    // Use lightweight count endpoint instead of fetching all scenario data
    this.scenarioService.getScenarioCountsByFunctionalArea(clientId).subscribe({
      next: (result) => {
        const tabs = result.counts.map(c => ({
          name: c.functionalAreaName || c.functionalAreaId,
          id: c.functionalAreaId,
          count: c.count
        })).sort((a, b) => b.count - a.count);
        this.faTabCounts.set(tabs);
        this.allScenariosCount.set(result.total);
        this._allClientScenarios = [];
        // Cache for instant display next time
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            tabs,
            total: result.total
          }));
        } catch { /* storage full, ignore */ }
      },
      error: () => {
        this.faTabCounts.set([]);
        this._allClientScenarios = [];
        this.allScenariosCount.set(0);
      }
    });
  }

  public rebuildFaTabCounts(): void {
    if (this._allClientScenarios.length === 0) {
      this.loadFaTabCounts();
      return;
    }

    let filtered = this._allClientScenarios;

    const testTypeFilter = this.filterTestType();
    if (testTypeFilter.length > 0) {
      filtered = filtered.filter(s =>
        testTypeFilter.includes(s.testTypeId || s.testType?.id || '')
      );
    }

    const search = (this.searchTerm || '').toLowerCase().trim();
    if (search) {
      filtered = filtered.filter(s =>
        (s.scenarioName || '').toLowerCase().includes(search) ||
        (s.scenarioId || '').toLowerCase().includes(search) ||
        (s.scenarioDescription || '').toLowerCase().includes(search) ||
        (s.functionalArea?.name || '').toLowerCase().includes(search)
      );
    }

    const countMap = new Map<string, { name: string; id: string; count: number }>();
    for (const s of filtered) {
      if (s.functionalArea?.id && s.functionalArea?.name) {
        const existing = countMap.get(s.functionalArea.id);
        if (existing) {
          existing.count++;
        } else {
          countMap.set(s.functionalArea.id, {
            name: s.functionalArea.name,
            id: s.functionalArea.id,
            count: 1
          });
        }
      }
    }
    const tabs = Array.from(countMap.values()).sort((a, b) => b.count - a.count);
    this.faTabCounts.set(tabs);
    this.allScenariosCount.set(filtered.length);
  }

  public selectFaTab(tabId: string): void {
    this.activeFaTab.set(tabId);
    if (tabId === 'all') {
      this.selectedFunctionalAreaFilter.set('');
    } else {
      this.selectedFunctionalAreaFilter.set(tabId);
    }
    this.currentPage = 1;
    this.loadScenariosPaginated();
  }

  public toggleMoreDropdown(): void {
    this.showMoreDropdown.set(!this.showMoreDropdown());
  }

  public closeMoreDropdown(): void {
    this.showMoreDropdown.set(false);
  }

  public get visibleTabs() {
    return this.faTabCounts().slice(0, this.visibleTabLimit);
  }

  public get overflowTabs() {
    return this.faTabCounts().slice(this.visibleTabLimit);
  }

  public get filteredOverflowTabs() {
    if (!this.moreDropdownSearch) return this.overflowTabs;
    const q = this.moreDropdownSearch.toLowerCase();
    return this.overflowTabs.filter(t => t.name.toLowerCase().includes(q));
  }

  public getFaBadgeClass(faName: string): string {
    const name = (faName || '').toLowerCase();
    if (name.includes('hcm') || name.includes('human capital')) return 'b-hcm';
    if (name.includes('payroll') || name.includes('pay')) return 'b-pay';
    if (name.includes('benefit')) return 'b-ben';
    if (name.includes('time')) return 'b-tt';
    if (name.includes('student') || name.includes('academic')) return 'b-stu';
    if (name.includes('compens')) return 'b-comp';
    if (name.includes('recruit')) return 'b-rec';
    if (name.includes('security')) return 'b-sec';
    if (name.includes('absence')) return 'b-abs';
    if (name.includes('talent')) return 'b-tal';
    if (name.includes('integr')) return 'b-int';
    if (name.includes('financ') || name.includes('account')) return 'b-fin';
    if (name.includes('procur')) return 'b-proc';
    if (name.includes('report')) return 'b-rep';
    return 'b-default';
  }


  public filterByFunctionalArea(functionalAreaId: string): void {
    this.selectedFunctionalAreaFilter.set(functionalAreaId);

    if (functionalAreaId) {
      sessionStorage.setItem('scenario_functionalAreaId', functionalAreaId);
    } else {
      sessionStorage.removeItem('scenario_functionalAreaId');
    }

    this.currentPage = 1;
    this.loadScenariosPaginated();
  }

  public clearFunctionalAreaFilter(): void {
    this.selectedFunctionalAreaFilter.set('');
    sessionStorage.removeItem('scenario_functionalAreaId');

    this.currentPage = 1;
    this.loadScenariosPaginated();
  }

  public testTypeFilterOptions() {
    return this.type_test.map(item => ({
      label: item.referenceValue,
      value: item.id
    }));
  }


  private loadAuthProfiles(functionalAreaId?: string): void {
    if (functionalAreaId) {
      // Load grouped auth profiles by functional area
      this.authProfileService.getAuthProfilesByFunctionalArea(functionalAreaId).subscribe({
        next: (groupedData: any) => {
          // Store full grouped data for execution resolution
          const groupedArray = Object.keys(groupedData).map(groupName => ({
            label: groupName,
            value: groupName,
            items: groupedData[groupName] || []
          }));
          this.groupedAuthProfiles.set(groupedArray);

          // Build deduplicated profile group options for dropdown
          const groupOptions = Object.keys(groupedData)
            .filter(groupName => groupName !== 'default')
            .map(groupName => ({
              label: `${groupName} (${(groupedData[groupName] || []).length} profiles)`,
              value: groupName
            }));
          this.profileGroupOptions.set(groupOptions);
        },
        error: (error) => {
          this.toastService.error('Failed to load authentication profiles');
          this.groupedAuthProfiles.set([]);
          this.profileGroupOptions.set([]);
        }
      });
    } else {
      // Load all auth profiles (ungrouped) - for backward compatibility
      this.authProfileService.getAllAuthProfiles().subscribe({
        next: (profiles: AuthProfile[]) => {
          this.authProfiles.set(profiles || []);
        },
        error: (error) => {
          this.toastService.error('Failed to load authentication profiles');
        }
      });
    }
  }

  // Handler for functional area change in dialog
  public onFunctionalAreaChange(functionalAreaId: string): void {
    if (functionalAreaId) {
      this.loadAuthProfiles(functionalAreaId);
      // Clear the auth profile selection when functional area changes
      this.form.patchValue({ authProfileId: null, selectedProfileGroup: null });
    } else {
      this.groupedAuthProfiles.set([]);
      this.profileGroupOptions.set([]);
      this.form.patchValue({ authProfileId: null, selectedProfileGroup: null });
    }
  }

  private loadScreenMasters(): void {
    this.screenMasterService.getAllScreenMaster().subscribe({
      next: (screens: Screen[]) => {
        this.screenMasters.set(screens || []);
      },
      error: (error) => {
        this.toastService.error('Failed to load screen masters');
      }
    });
  }

  private loadUserFunctionalAreas(callback?: () => void): void {
    this.scenarioService.getUserFunctionalAreas().subscribe({
      next: (response: any) => {
        this.all_function_areas = Array.isArray(response) ? response : [];
        this.filtered_function_area = this.getClientFilteredFunctionalAreas();
        callback?.();
      },
      error: (error) => {
        this.toastService.error('Failed to load functional areas');
        this.all_function_areas = [];
        this.filtered_function_area = [];
        callback?.();
      }
    });
  }

  public clientFilterOptions() {
    return this.clientOptions(); // Use signal getter
  }

  public filterByClient(clientId: string): void {
    if (!clientId && this.isSystemUser()) {
      clientId = 'default';
    }
    
    this.selectedClientFilter.set(clientId);

    // Save to sessionStorage for persistence when navigating to test recorder (including 'default')
    if (clientId) {
      sessionStorage.setItem('scenario_clientId', clientId);
    } else {
      sessionStorage.removeItem('scenario_clientId');
    }

    // Clear all filters when client changes
    this.selectedFunctionalAreaFilter.set('');
    this.filterTestType.set([]);
    sessionStorage.removeItem('scenario_functionalAreaId');
    sessionStorage.removeItem('scenario_testTypeId');

    if (!clientId) {
      this.filtered_function_area = [];
      this.allScenarios.set([]);
      this.scenarios.set([]);
      return;
    }

    this.filtered_function_area = this.getClientFilteredFunctionalAreas(clientId);
    this.activeFaTab.set('all');
    this.currentPage = 1;
    this.loadScenariosPaginated();
    this.loadFaTabCounts();
  }

  private extractUniqueFunctionalAreasFromScenarios(scenarios: Scenario[]): void {
    const functionalAreasMap = new Map();
    
    for (const scenario of scenarios) {
      if (scenario.functionalArea && scenario.functionalArea.id) {
        const area: any = { ...scenario.functionalArea };
        
        // Ensure testType is included - copy from scenario if not present
        if (!area.testType && scenario.testType) {
          area.testType = scenario.testType;
        }
        
        // Also set testTypeId as fallback
        if (!area.testTypeId && scenario.testTypeId) {
          area.testTypeId = scenario.testTypeId;
        }
        
        if (!functionalAreasMap.has(area.id)) {
          functionalAreasMap.set(area.id, area);
        }
      }
    }
    
    this.filtered_function_area = Array.from(functionalAreasMap.values());
  
  }

  public clearClientFilter(): void {
    if (this.isSystemUser()) {
      this.selectedClientFilter.set('default');
      this.selectedFunctionalAreaFilter.set('');
      this.filterTestType.set([]);
      this.activeFaTab.set('all');

      sessionStorage.setItem('scenario_clientId', 'default');
      sessionStorage.removeItem('scenario_functionalAreaId');
      sessionStorage.removeItem('scenario_testTypeId');

      this.filtered_function_area = this.getClientFilteredFunctionalAreas('default');
      this.currentPage = 1;
      this.loadScenariosPaginated();
      return;
    }
    
    if (this.isClientDropdownDisabled()) {
      // Single client user: Don't allow clearing
      return;
    }
    
    // Multi-client user: Clear selection
    this.selectedClientFilter.set('');
    this.selectedFunctionalAreaFilter.set('');
    this.filterTestType.set([]);
    this.filtered_function_area = [];
    this.allScenarios.set([]);
    this.scenarios.set([]);

    // Clear sessionStorage
    sessionStorage.removeItem('scenario_clientId');
    sessionStorage.removeItem('scenario_functionalAreaId');
    sessionStorage.removeItem('scenario_testTypeId');
  }

  isSystemUser(): boolean {
    const userInfo = this.authService.getUserInfoFromToken();
    return userInfo?.isSystemUser === true;
  }

  /**
   * Export scenario steps to Excel
   * Fetches steps for the scenario and exports to Excel file
   */
  public exportScenarioSteps(scenarioData: Scenario): void {
    if (!scenarioData || !scenarioData.id) {
      this.toastService.error('Unable to export: Scenario data is missing');
      return;
    }

    this.scenarioService.getScenarioSteps(scenarioData.id).subscribe({
      next: async (response: any) => {
        try {
          const steps = Array.isArray(response) ? response : (response?.data || []);
          await this.exportService.exportScenarioStepsToExcel(scenarioData, steps);
          this.toastService.success(`Scenario "${scenarioData.scenarioName}" exported successfully`);
        } catch (error) {
          this.toastService.error('Failed to export scenario');
        }
      },
      error: (error) => {
        this.toastService.error('Failed to load scenario steps for export');
      }
    });
  }

  public filterByTestType(testTypeIds: string[] | string): void {
    const testTypeArray = Array.isArray(testTypeIds) ? testTypeIds : (testTypeIds ? [testTypeIds] : []);

    this.filterTestType.set(testTypeArray);

    if (testTypeArray.length > 0) {
      sessionStorage.setItem('scenario_testTypeId', testTypeArray.join(','));
    } else {
      sessionStorage.removeItem('scenario_testTypeId');
    }

    this.selectedFunctionalAreaFilter.set('');
    sessionStorage.removeItem('scenario_functionalAreaId');

    if (testTypeArray.length > 0) {
      this.applyFunctionalAreaFilter(this.selectedClientFilter());
    } else {
      this.filtered_function_area = [];
    }

    this.currentPage = 1;
    this.loadScenariosPaginated();
    this.rebuildFaTabCounts();
  }

  public clearTestTypeFilter(): void {
    this.filterTestType.set([]);
    this.selectedFunctionalAreaFilter.set('');
    sessionStorage.removeItem('scenario_functionalAreaId');
    sessionStorage.removeItem('scenario_testTypeId');

    this.filtered_function_area = [];
    this.currentPage = 1;
    this.loadScenariosPaginated();
    this.rebuildFaTabCounts();
  }

}